/**
 * Configuración centralizada de rutas
 *
 * Single Source of Truth (SSOT) para:
 * - Paths, títulos, permisos, breadcrumbs
 * - Qué rutas están activas (enabled)
 * - Orden y agrupación del sidebar (category + order)
 *
 * El sidebar se arma desde aquí; el router registra solo rutas enabled
 * que tengan componente en `route-pages.tsx`.
 */

import { PERMISOS } from "@/constants/permissions";
import type { BreadcrumbItemType } from "@/components/ui/breadcrumb";

export type RouteCategory =
  | "gestión"
  | "novedades"
  | "negociaciones"
  | "reportes"
  | "administrativo"
  | "maestros";

export interface RouteConfig {
  /** Ruta de la aplicación (ej: "/filing") */
  path: string;
  /** Título para mostrar en el sidebar */
  title: string;
  /** Etiqueta para el breadcrumb */
  breadcrumbLabel: string;
  /** Permiso requerido para acceder a esta ruta */
  permission: string;
  /** Datos maestros necesarios para esta ruta */
  masterData: string[];
  /** Categoría de la ruta (para agrupar en el sidebar) */
  category?: RouteCategory;
  /**
   * Si es false, no aparece en sidebar ni se registra en el router.
   * Por defecto true.
   */
  enabled?: boolean;
  /** Orden dentro de la categoría del sidebar (menor = primero) */
  order?: number;
  /** Se registra en el router pero no aparece en el sidebar (ej: rutas de detalle). */
  hideInSidebar?: boolean;
  /** Ruta padre: se agrega como enlace en el breadcrumb antes de esta ruta. */
  parentId?: string;
}

export const ROUTES = {
  filing: {
    path: "/filing",
    title: "Facturas",
    breadcrumbLabel: "Facturas",
    permission: PERMISOS.RADICACION_DE_FACTURAS.VER_RADICACION,
    masterData: [],
    category: "gestión",
    order: 10,
  },
  flatFiles: {
    path: "/flat-files",
    title: "Consolidados",
    breadcrumbLabel: "Consolidados",
    permission: PERMISOS.RADICACION_DE_FACTURAS.RADICAR_FACTURAS,
    masterData: [],
    category: "gestión",
    order: 20,
  },
  manualConsolidatedFlatFiles: {
    path: "/flat-files-manual",
    title: "Consolidados manuales",
    breadcrumbLabel: "Consolidados manuales",
    permission: PERMISOS.RADICACION_DE_FACTURAS.CONSOLIDADOS_MANUALES_VER,
    masterData: [],
    category: "gestión",
    order: 30,
  },
  support: {
    path: "/delivery-supports",
    title: "Soportes de entrega",
    breadcrumbLabel: "Soportes de entrega",
    permission: PERMISOS.RADICACION_DE_FACTURAS.SOPORTE_ENTREGA_VER,
    masterData: [],
    category: "gestión",
    order: 40,
  },
  deliverySupportReplacement: {
    path: "/delivery-support-replacement",
    title: "Reemplazo soportes entrega",
    breadcrumbLabel: "Reemplazo soportes entrega",
    permission: PERMISOS.RADICACION_DE_FACTURAS.SOPORTE_ENTREGA_REEMPLAZAR,
    masterData: [],
    category: "gestión",
    order: 50,
  },
  syncDeliverySupport: {
    path: "/sync-delivery-support",
    title: "Sincronizar soportes entrega",
    breadcrumbLabel: "Sincronizar soportes entrega",
    permission: PERMISOS.RADICACION_DE_FACTURAS.SINCRONIZACION_SOPORTES_ENTREGA,
    masterData: [],
    category: "gestión",
    order: 60,
    enabled: false,
  },
  notifyNovelty: {
    path: "/notify-novelty",
    title: "Novedades",
    breadcrumbLabel: "Novedades",
    permission: PERMISOS.RADICACION_DE_FACTURAS.NOTIFICAR_NOVEDAD_VER,
    masterData: [],
    category: "novedades",
    order: 10,
  },
  suraNovelties: {
    path: "/sura-novelties",
    title: "Novedades Sura",
    breadcrumbLabel: "Novedades Sura",
    permission: PERMISOS.RADICACION_DE_FACTURAS.NOTIFICAR_NOVEDAD_SURA_VER,
    masterData: [],
    category: "novedades",
    order: 20,
  },
  manualFlatFile: {
    path: "/manual-flat-file",
    title: "Archivo plano manual",
    breadcrumbLabel: "Archivo plano manual",
    permission: PERMISOS.RADICACION_DE_FACTURAS.CONSOLIDADOS_MANUALES_CARGAR,
    masterData: [],
    category: "gestión",
    order: 90,
    enabled: false,
  },
  "billing-consolidated": {
    path: "/billing-consolidated",
    title: "Consolidado facturación",
    breadcrumbLabel: "Consolidado facturación",
    permission: PERMISOS.RADICACION_DE_FACTURAS.CONSOLIDADO_FACTURACION_VER,
    masterData: [],
    category: "reportes",
    order: 10,
    enabled: false,
  },
  "pending-billing": {
    path: "/pending-billing",
    title: "Consolidado pendientes",
    breadcrumbLabel: "Consolidado pendientes",
    permission: PERMISOS.RADICACION_DE_FACTURAS.CONSOLIDADO_PENDIENTES_VER,
    masterData: [],
    category: "reportes",
    order: 20,
    enabled: false,
  },
  "filed-report": {
    path: "/filed-report",
    title: "Reporte de radicados",
    breadcrumbLabel: "Reporte de radicados",
    permission: PERMISOS.RADICACION_DE_FACTURAS.REPORTE_RADICACION_VER,
    masterData: [],
    category: "reportes",
    order: 30,
    enabled: false,
  },
  historicalInvoices: {
    path: "/historical-invoices",
    title: "Histórico facturas",
    breadcrumbLabel: "Histórico facturas",
    permission: PERMISOS.RADICACION_DE_FACTURAS.HISTORICO_FACTURAS_VER,
    masterData: [],
    category: "reportes",
    order: 40,
    enabled: false,
  },
  filingRadicationDashboard: {
    path: "/filing-radication-dashboard",
    title: "Dashboard",
    breadcrumbLabel: "Dashboard",
    permission: PERMISOS.RADICACION_DE_FACTURAS.VER_RADICACION,
    masterData: [],
    category: "reportes",
    order: 50,
  },
  canceledReport: {
    path: "/canceled-report",
    title: "Reporte de Anulados",
    breadcrumbLabel: "Reporte de Anulados",
    permission: PERMISOS.RADICACION_DE_FACTURAS.REPORTE_ANULADOS_VER,
    masterData: [],
    category: "reportes",
    order: 60,
  },
  otherPlansReport: {
    path: "/other-plans-report",
    title: "Reporte otros planes",
    breadcrumbLabel: "Reporte otros planes",
    permission: PERMISOS.RADICACION_DE_FACTURAS.REPORTE_OTROS_PLANES_VER,
    masterData: [],
    category: "reportes",
    order: 70,
  },
  missingDeliverySupportsReport: {
    path: "/missing-delivery-supports-report",
    title: "Reporte soportes faltantes",
    breadcrumbLabel: "Reporte soportes faltantes",
    permission: PERMISOS.RADICACION_DE_FACTURAS.SOPORTE_ENTREGA_VER,
    masterData: [],
    category: "reportes",
    order: 80,
  },
  bulkFlatFile: {
    path: "/administrativo/archivo-plano-masivo",
    title: "Archivo plano masivo",
    breadcrumbLabel: "Archivo plano masivo",
    permission: PERMISOS.ADMINISTRATIVO.ARCHIVO_PLANO_MASIVO_GENERAR,
    masterData: [],
    category: "administrativo",
    order: 10,
  },
  syncJobs: {
    path: "/administrativo/sync-jobs",
    title: "Sincronización cruce de cuentas",
    breadcrumbLabel: "Sincronización cruce de cuentas",
    permission: PERMISOS.ADMINISTRATIVO.VER_SINCRONIZACIONES,
    masterData: [],
    category: "administrativo",
    order: 20,
  },
  syncJobDetail: {
    path: "/administrativo/sync-jobs/:executionId",
    title: "Detalle de ejecución",
    breadcrumbLabel: "Detalle de ejecución",
    permission: PERMISOS.ADMINISTRATIVO.VER_SINCRONIZACIONES,
    masterData: [],
    category: "administrativo",
    hideInSidebar: true,
    parentId: "syncJobs",
  },
  cityCodes: {
    path: "/maestros/codigos-ciudad",
    title: "Códigos ciudad",
    breadcrumbLabel: "Códigos ciudad",
    permission: PERMISOS.MAESTROS.CODIGOS_CIUDAD_VER,
    masterData: [],
    category: "maestros",
    order: 10,
  },
  users: {
    path: "/maestros/usuarios",
    title: "Usuarios",
    breadcrumbLabel: "Usuarios",
    permission: PERMISOS.MAESTROS.USUARIOS_VER,
    masterData: [],
    category: "maestros",
    order: 20,
  },
  pharmacyDirectories: {
    path: "/maestros/pharmacy-directories",
    title: "Directorios farmacia",
    breadcrumbLabel: "Directorios farmacia",
    permission: PERMISOS.MAESTROS.DIRECTORIOS_FARMACIA_VER,
    masterData: [],
    category: "maestros",
    order: 30,
  },
  affectationTypes: {
    path: "/maestros/tipos-afectacion",
    title: "Tipos de afectación",
    breadcrumbLabel: "Tipos de afectación",
    permission: PERMISOS.MAESTROS.TIPOS_AFECTACION_VER,
    masterData: [],
    category: "maestros",
    order: 35,
  },
  ironPortfolios: {
    path: "/maestros/iron-portfolios",
    title: "Carteras hierro",
    breadcrumbLabel: "Carteras hierro",
    permission: PERMISOS.MAESTROS.IRON_PORTFOLIOS_VER,
    masterData: [],
    category: "maestros",
    order: 40,
  },
  portfolioMasters: {
    path: "/maestros/portfolio-masters",
    title: "Maestro de carteras",
    breadcrumbLabel: "Maestro de carteras",
    permission: PERMISOS.MAESTROS.IRON_PORTFOLIOS_VER,
    masterData: [],
    category: "maestros",
    order: 45,
  },
  suraInconsistencyHomologations: {
    path: "/maestros/homologaciones-inconsistencias-sura",
    title: "Homologaciones Sura",
    breadcrumbLabel: "Homologaciones Sura",
    permission: PERMISOS.MAESTROS.HOMOLOGACIONES_INCONSISTENCIAS_SURA_VER,
    masterData: [],
    category: "maestros",
    order: 50,
  },
} as const satisfies Record<string, RouteConfig>;

export type RouteId = keyof typeof ROUTES;

export type RouteEntry = {
  id: RouteId;
  route: RouteConfig;
};

function isRouteEnabled(route: RouteConfig): boolean {
  return route.enabled !== false;
}

/**
 * Obtiene la configuración de una ruta por su path
 */
export function getRouteConfig(path: string): RouteConfig | undefined {
  return Object.values(ROUTES).find((route) => route.path === path);
}

/**
 * Entradas de ruta (id + config) de una categoría, solo enabled, ordenadas.
 */
export function getRouteEntriesByCategory(category: RouteCategory): RouteEntry[] {
  return (Object.entries(ROUTES) as [RouteId, RouteConfig][])
    .filter(
      ([, route]) =>
        route.category === category &&
        isRouteEnabled(route) &&
        !route.hideInSidebar
    )
    .sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999))
    .map(([id, route]) => ({ id, route }));
}

/**
 * Todas las rutas enabled (para registrar en el router).
 */
export function getEnabledRouteEntries(): RouteEntry[] {
  return (Object.entries(ROUTES) as [RouteId, RouteConfig][])
    .filter(([, route]) => isRouteEnabled(route))
    .map(([id, route]) => ({ id, route }));
}

const CATEGORY_BREADCRUMB_LABEL: Partial<Record<RouteCategory, string>> = {
  novedades: "Novedades",
  reportes: "Reportes",
  administrativo: "Administrativo",
  maestros: "Maestros",
};

/**
 * Genera el mapa de breadcrumbs desde la configuración de rutas
 */
export function getBreadcrumbMap(): Record<string, BreadcrumbItemType[]> {
  const breadcrumbMap: Record<string, BreadcrumbItemType[]> = {
    "/": [{ label: "Inicio" }, { label: "Facturas" }],
  };

  (Object.values(ROUTES) as RouteConfig[]).forEach((route) => {
    const items: BreadcrumbItemType[] = [{ label: "Inicio", href: "/" }];
    const groupLabel =
      route.category != null
        ? CATEGORY_BREADCRUMB_LABEL[route.category]
        : undefined;
    if (groupLabel && groupLabel !== route.breadcrumbLabel) {
      items.push({ label: groupLabel });
    }
    const parent =
      route.parentId != null
        ? (ROUTES as Record<string, RouteConfig>)[route.parentId]
        : undefined;
    if (parent) {
      items.push({ label: parent.breadcrumbLabel, href: parent.path });
    }
    items.push({ label: route.breadcrumbLabel });
    breadcrumbMap[route.path] = items;
  });

  return breadcrumbMap;
}

/**
 * Genera el mapa de datos maestros desde la configuración de rutas
 */
export function getMasterDataMap(): Record<string, string[]> {
  const masterDataMap: Record<string, string[]> = {};

  Object.values(ROUTES).forEach((route) => {
    masterDataMap[route.path] = route.masterData;
  });

  return masterDataMap;
}

/**
 * Obtiene todas las rutas enabled de una categoría específica
 */
export function getRoutesByCategory(category: RouteCategory): RouteConfig[] {
  return getRouteEntriesByCategory(category).map(({ route }) => route);
}

/** Path relativo para React Router anidado (sin "/" inicial) */
export function toRelativeRoutePath(path: string): string {
  return path.replace(/^\//, "");
}
